import { makeObservable, observable, action, runInAction } from "mobx";
import {
  InventoryListingDetails,
  InventoryOpsPoDetail,
  PrintInward,
  ProjectInventoryOpsProductDetails,
  ProjectInventoryOpsSummary,
  VendorInvoiceDetails,
} from "../../models/ProjectManagement/ProjectInventoryProduct";
import ProjectInventoryOpsAPI from "../../api/ProjectInventoryOpsAPI";
import {
  PrintInwardQcReq,
  PrintInwardReq,
  PrintOutwardReq,
  RawInwardQcReq,
  RawInwardReq,
  RawInwardDetailsReq,
  PrintInwardDetailsReq,
  VendorInvoice,
  VendorInvoiceType,
} from "../../models/ProjectManagement/InventoryOpsRequests";
import { PurchaseOrder } from "../../models/ProjectManagement/PurchaseOrder";
import dayjs, { Dayjs } from "dayjs";

export class ProjectInventoryOpsStore {
  projectInventoryOpsAPI: ProjectInventoryOpsAPI;
  originalData: InventoryListingDetails;
  inventoryRequestBody: InventoryListingDetails;
  productDetails: ProjectInventoryOpsProductDetails;
  productDetailsBody: ProjectInventoryOpsProductDetails;
  summaryDetails: ProjectInventoryOpsSummary;
  currentPoDetails: InventoryOpsPoDetail;
  currentProductId: number | null = null;
  dialogType: "rawInward" | "printInward" | null = null;
  vendorInvoiceDetails: VendorInvoiceDetails;

  constructor() {
    this.projectInventoryOpsAPI = new ProjectInventoryOpsAPI();
    this.originalData = {} as InventoryListingDetails;
    this.inventoryRequestBody = {} as InventoryListingDetails;
    this.productDetails = {} as ProjectInventoryOpsProductDetails;
    this.productDetailsBody = {} as ProjectInventoryOpsProductDetails;
    this.summaryDetails = {} as ProjectInventoryOpsSummary;
    this.currentPoDetails = {} as InventoryOpsPoDetail;
    this.vendorInvoiceDetails = {};

    makeObservable(this, {
      originalData: observable,
      inventoryRequestBody: observable,
      productDetails: observable,
      productDetailsBody: observable,
      summaryDetails: observable,
      currentPoDetails: observable,
      dialogType: observable,
      getProjectInventoryOpsDetails: action,
      getProjectInventoryOpsProductDetails: action,
      getSummary: action,
      setCurrentPoDetails: action,
      updateCurrentPoDetails: action,
      updateQcStartStatus: action,
      clearPopupData: action,
      updateRawInwardDate: action,
      updatePrintInwardDate: action,
      setDialogType: action,
      setCurrentProductId: action,
      deleteRawInwardQCImg: action,
      deletePrintInwardQCImg: action,
      vendorInvoiceDetails: observable,
      fetchVendorInvoices: action,
      addUpdateVendorInvoices: action,
      updateInvoiceMarkedAsReceived: action,
    });
  }

  getProjectInventoryOpsDetails = async (projectId: number) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.getInventoryOpsProducts(projectId);
      this.originalData = response;
      this.inventoryRequestBody = response;
    } catch (error: any) {
      throw error;
    }
  };

  getProjectInventoryOpsProductDetails = async (
    projectId: number,
    productId: number
  ) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.getProjectInventoryOpsProductDetails(
          projectId,
          productId
        );
      this.productDetails = response;
      this.productDetailsBody = response;
    } catch (error: any) {
      throw error;
    }
  };

  setCurrentPoDetails = (poId: number) => {
    const inventoryDetail = this.productDetailsBody.inventory_ops_details.find(
      (podetails) => podetails.po_id === poId
    );

    const purchaseOrder = this.productDetailsBody.purchase_orders.find(
      (po) => po.id === poId
    );

    this.currentPoDetails = {
      ...(inventoryDetail || {}),
      po_id: poId,
      product_po_quantity: purchaseOrder ? purchaseOrder.quantity : 0,
      vendor_name: purchaseOrder ? purchaseOrder.vendor_name : "",
    };
  };

  checkProductIndex = (productId: number): number => {
    if (
      !this.inventoryRequestBody.products ||
      this.inventoryRequestBody.products.length === 0
    )
      return -1;

    const productList = this.inventoryRequestBody.products;
    const currentIndex = productList.findIndex(
      (product) => product.id === productId
    );

    return currentIndex;
  };

  updateRawInward = async (reqBody: RawInwardReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updateRawInward(
        reqBody
      );

      if (response) {
        const updatedDetail: InventoryOpsPoDetail | undefined =
          response.inventory_ops_details?.[0];
        if (updatedDetail) {
          const index = this.productDetailsBody.inventory_ops_details.findIndex(
            (detail) => detail.po_id === updatedDetail.po_id
          );
          if (index !== -1) {
            this.productDetailsBody.inventory_ops_details[index] = {
              ...updatedDetail,
            };
          } else {
            this.productDetailsBody.inventory_ops_details.push(updatedDetail);
          }

          this.setCurrentPoDetails(updatedDetail.po_id);

          this.updateCurrentPoDetails({
            temp_raw_inward_quantity: null,
          });

          this.productDetailsBody.progress = response.progress ?? 0;
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateRawInwardQc = async (reqBody: RawInwardQcReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updateRawInwardQc(
        reqBody
      );

      if (response) {
        const updatedDetail: InventoryOpsPoDetail | undefined =
          response.inventory_ops_details?.[0];
        if (updatedDetail) {
          const index = this.productDetailsBody.inventory_ops_details.findIndex(
            (detail) => detail.po_id === updatedDetail.po_id
          );
          if (index !== -1) {
            this.productDetailsBody.inventory_ops_details[index] = {
              ...updatedDetail,
            };
          } else {
            this.productDetailsBody.inventory_ops_details.push(updatedDetail);
          }

          this.setCurrentPoDetails(updatedDetail.po_id);

          this.updateCurrentPoDetails({
            temp_raw_inward_rejection_quantity: null,
            temp_raw_inward_rejection_reason: null,
            temp_raw_inward_qc_images: [],
          });
          this.productDetailsBody.progress = response.progress ?? 0;
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateRawInwardDetails = async (reqBody: RawInwardDetailsReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updateRawInwardDetails(
        reqBody
      );

      if (response) {
        const updatedDetail: InventoryOpsPoDetail | undefined =
          response.inventory_ops_details?.[0];
        if (updatedDetail) {
          const index = this.productDetailsBody.inventory_ops_details.findIndex(
            (detail) => detail.po_id === updatedDetail.po_id
          );
          if (index !== -1) {
            this.productDetailsBody.inventory_ops_details[index] = {
              ...updatedDetail,
            };
          } else {
            this.productDetailsBody.inventory_ops_details.push(updatedDetail);
          }

          this.setCurrentPoDetails(updatedDetail.po_id);

          // Clear all temp fields for both raw inward and QC
          this.updateCurrentPoDetails({
            temp_raw_inward_quantity: null,
            temp_raw_inward_rejection_quantity: null,
            temp_raw_inward_rejection_reason: null,
            temp_raw_inward_qc_images: [],
          });
          this.productDetailsBody.progress = response.progress ?? 0;
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintOutward = async (body: PrintOutwardReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updatePrintOutward(
        body
      );
      if (response) {
        const updatedDetail: InventoryOpsPoDetail | undefined =
          response.inventory_ops_details?.[0];
        if (updatedDetail) {
          const index = this.productDetailsBody.inventory_ops_details.findIndex(
            (detail) => detail.po_id === updatedDetail.po_id
          );
          if (index !== -1) {
            this.productDetailsBody.inventory_ops_details[index] = {
              ...updatedDetail,
            };
          } else {
            this.productDetailsBody.inventory_ops_details.push(updatedDetail);
          }

          this.setCurrentPoDetails(updatedDetail.po_id);

          this.updateCurrentPoDetails({
            temp_print_outward_printer_id: null,
            temp_print_outward_printer_name: null,
            temp_print_outward_quantity: null,
            temp_print_outward_timeline: null,
          });
          this.productDetailsBody.progress = response.progress ?? 0;
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintInward = async (body: PrintInwardReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updatePrintInward(
        body
      );

      if (response) {
        const updatedDetail: InventoryOpsPoDetail | undefined =
          response.inventory_ops_details?.[0];
        if (updatedDetail) {
          const index = this.productDetailsBody.inventory_ops_details.findIndex(
            (detail) => detail.po_id === updatedDetail.po_id
          );
          if (index !== -1) {
            this.productDetailsBody.inventory_ops_details[index] = {
              ...updatedDetail,
            };
          } else {
            this.productDetailsBody.inventory_ops_details.push(updatedDetail);
          }

          this.setCurrentPoDetails(updatedDetail.po_id);

          this.updateCurrentPoDetails({
            temp_print_inward_quantity: null,
          });
          this.productDetailsBody.progress = response.progress ?? 0;
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintInwardQc = async (body: PrintInwardQcReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updatePrintInwardQc(
        body
      );

      if (response) {
        const updatedDetail: InventoryOpsPoDetail | undefined =
          response.inventory_ops_details?.[0];
        if (updatedDetail) {
          const index = this.productDetailsBody.inventory_ops_details.findIndex(
            (detail) => detail.po_id === updatedDetail.po_id
          );
          if (index !== -1) {
            this.productDetailsBody.inventory_ops_details[index] = {
              ...updatedDetail,
            };
          } else {
            this.productDetailsBody.inventory_ops_details.push(updatedDetail);
          }

          this.setCurrentPoDetails(updatedDetail.po_id);

          this.updateCurrentPoDetails({
            temp_print_inward_rejection_quantity: null,
            temp_print_inward_rejection_reason: null,
            temp_print_inward_qc_images: [],
          });
          this.productDetailsBody.progress = response.progress ?? 0;
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintInwardDetails = async (body: PrintInwardDetailsReq) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.updatePrintInwardDetails(body);

      if (response) {
        const updatedDetail: InventoryOpsPoDetail | undefined =
          response.inventory_ops_details?.[0];
        if (updatedDetail) {
          const index = this.productDetailsBody.inventory_ops_details.findIndex(
            (detail) => detail.po_id === updatedDetail.po_id
          );
          if (index !== -1) {
            this.productDetailsBody.inventory_ops_details[index] = {
              ...updatedDetail,
            };
          } else {
            this.productDetailsBody.inventory_ops_details.push(updatedDetail);
          }

          this.setCurrentPoDetails(updatedDetail.po_id);

          this.updateCurrentPoDetails({
            temp_print_inward_quantity: null,
            temp_print_inward_rejection_quantity: null,
            temp_print_inward_rejection_reason: null,
            temp_print_inward_qc_images: [],
          });
          this.productDetailsBody.progress = response.progress ?? 0;
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  updateCurrentPoDetails = (fields: Partial<typeof this.currentPoDetails>) => {
    runInAction(() => {
      this.currentPoDetails = { ...this.currentPoDetails, ...fields };
    });
  };

  updateQcStartStatus = (
    field: "raw_inward_in_progress" | "print_inward_in_progress",
    value: boolean
  ) => {
    this.updateCurrentPoDetails({
      [field]: value,
    });

    const poId = this.currentPoDetails.po_id;
    const index = this.productDetailsBody.inventory_ops_details.findIndex(
      (detail) => detail.po_id === poId
    );

    runInAction(() => {
      if (index !== -1) {
        this.productDetailsBody.inventory_ops_details[index] = {
          ...this.productDetailsBody.inventory_ops_details[index],
          [field]: value,
        };
      } else {
        this.productDetailsBody.inventory_ops_details.push({
          po_id: poId,
          [field]: value,
        });
      }
    });
  };

  startRawInwardQc = async (body: RawInwardQcReq) => {
    try {
      await this.projectInventoryOpsAPI.startRawInwardQc(body);
    } catch (error: any) {
      throw error;
    }
  };

  startPrintInwardQc = async (body: PrintInwardQcReq) => {
    try {
      await this.projectInventoryOpsAPI.startPrintInwardQc(body);
    } catch (error: any) {
      throw error;
    }
  };

  setCurrentProductId = (productId: number | null) => {
    this.currentProductId = productId;
  };

  clearPopupData = () => {
    this.dialogType = null;
  };

  setDialogType = (type: "rawInward" | "printInward" | null) => {
    this.dialogType = type;
  };

  updateRawInwardDate = (newValue: Dayjs | null, index: number) => {
    if (!this.currentProductId) return;

    const productIndex = this.inventoryRequestBody.products?.findIndex(
      (product) => product.id === this.currentProductId
    );

    if (
      productIndex !== -1 &&
      this.inventoryRequestBody.products?.[productIndex]?.purchase_orders
    ) {
      runInAction(() => {
        this.inventoryRequestBody.products![productIndex].purchase_orders[
          index
        ].expected_delivery_date = newValue
          ? dayjs(newValue).format("YYYY-MM-DD 00:00:00")
          : undefined;
      });
    }
  };

  updatePrintInwardDate = (newValue: Dayjs | null, index: number) => {
    if (!this.currentProductId) return;

    const productIndex = this.inventoryRequestBody.products?.findIndex(
      (product) => product.id === this.currentProductId
    );

    if (
      productIndex !== -1 &&
      this.inventoryRequestBody.products?.[productIndex]?.print_inward_timelines
    ) {
      runInAction(() => {
        this.inventoryRequestBody.products![
          productIndex
        ].print_inward_timelines[index].timeline = newValue
          ? dayjs(newValue).format("YYYY-MM-DD 00:00:00")
          : undefined;
      });
    }
  };

  updateRawInwardExpectedDate = async (
    order_id: number,
    productId: number,
    poData: PurchaseOrder[]
  ) => {
    try {
      const payload = {
        order_id: order_id,
        product_id: productId,
        purchase_requests: poData
          .filter(
            (po) =>
              po.pr_id !== undefined && po.expected_delivery_date !== undefined
          )
          .map((po) => ({
            pr_id: po.pr_id as number,
            expected_delivery_date: po.expected_delivery_date as string,
          })),
      };

      await this.projectInventoryOpsAPI.updateRawInwardExpectedDate(payload);
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintInwardTimeline = async (
    order_id: number,
    productId: number,
    poData: PrintInward[]
  ) => {
    try {
      const payload = {
        order_id: order_id,
        product_id: productId,
        raw_inwards: poData
          .filter(
            (po) => po.raw_inward_id !== undefined && po.po_id !== undefined
          )
          .map((po) => ({
            raw_inward_id: po.raw_inward_id as number,
            timeline: po.timeline as string,
            po_id: po.po_id as number,
          })),
      };

      await this.projectInventoryOpsAPI.updatePrintInwardTimeline(payload);
    } catch (error: any) {
      throw error;
    }
  };

  getSummary = async (projectId: number, productId: number) => {
    try {
      const response = await this.projectInventoryOpsAPI.getSummary(
        projectId,
        productId
      );
      this.summaryDetails = response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteRawInwardQCImg = async (id: number) => {
    try {
      if (id) {
        const response = await this.projectInventoryOpsAPI.deleteRawInwardQCImg(
          id
        );
        if (response.success) {
          if (
            this.currentPoDetails &&
            this.currentPoDetails.raw_inward_qc_images
          ) {
            this.currentPoDetails.raw_inward_qc_images =
              this.currentPoDetails.raw_inward_qc_images.filter(
                (image) => image.id !== id
              );
          }
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  deletePrintInwardQCImg = async (id: number) => {
    try {
      if (id) {
        const response =
          await this.projectInventoryOpsAPI.deletePrintInwardQCImg(id);
        if (response) {
          if (
            this.currentPoDetails &&
            this.currentPoDetails?.print_inward_qc_images
          ) {
            this.currentPoDetails.print_inward_qc_images =
              this.currentPoDetails.print_inward_qc_images.filter(
                (image) => image.id !== id
              );
          }
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  fetchVendorInvoices = async (inwardId: number, type: VendorInvoiceType) => {
    try {
      const response = await this.projectInventoryOpsAPI.fetchVendorInvoices(
        inwardId,
        type
      );
      this.vendorInvoiceDetails = response;
    } catch (error: any) {
      throw error;
    }
  };

  addUpdateVendorInvoices = async (
    inwardId: number,
    type: VendorInvoiceType,
    body: VendorInvoice[]
  ) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.addUpdateVendorInvoices(
          inwardId,
          type,
          body
        );
      this.vendorInvoiceDetails = response;
    } catch (error: any) {
      throw error;
    }
  };

  updateInvoiceMarkedAsReceived = async (
    inwardId: number,
    type: VendorInvoiceType,
    markedAsReceived: boolean
  ) => {
    try {
      await this.projectInventoryOpsAPI.updateInvoiceMarkedAsReceived(
        inwardId,
        type,
        markedAsReceived
      );
    } catch (error: any) {
      throw error;
    }
  };
}
